"use client"
import { useState, useEffect, useCallback } from "react"
import { useAuth } from "../contexts/AuthContext"
import { Link } from "react-router-dom"
import api from "../api"

const EmployeeDashboard = () => {
    const { user, logout } = useAuth()
    const [stats, setStats] = useState({
        assignedAssets: 0,
        pendingRequests: 0,
        approvedRequests: 0,
        totalRequests: 0,
        myRequests: []
    })
    const [loading, setLoading] = useState(true)

    // Room Registration Modal State
    const [showRoomModal, setShowRoomModal] = useState(false)
    const [locations, setLocations] = useState([])
    const [rooms, setRooms] = useState([])
    const [selectedLocation, setSelectedLocation] = useState("")
    const [selectedRoom, setSelectedRoom] = useState("")
    const [registering, setRegistering] = useState(false)

    const fetchDashboardData = useCallback(async () => {
        try {
            const dashboardRes = await api.get("/employee/dashboard")
            setStats({
                assignedAssets: dashboardRes.data.assignedAssets || 0,
                pendingRequests: dashboardRes.data.pendingRequests || 0,
                approvedRequests: dashboardRes.data.approvedRequests || 0,
                totalRequests: dashboardRes.data.totalRequests || 0,
                myRequests: dashboardRes.data.myRequests || []
            })
        } catch (error) {
            console.error("Error fetching dashboard data:", error)
            if (error.response?.status === 403) logout()
        } finally {
            setLoading(false)
        }
    }, [logout])

    useEffect(() => {
        if(user) fetchDashboardData()
    }, [fetchDashboardData, user])

    // Load locations when modal opens
    useEffect(() => {
        if (showRoomModal) {
            fetchLocations()
        }
    }, [showRoomModal])

    const fetchLocations = async () => {
        try {
            const response = await api.get("/locations")
            setLocations(response.data)
        } catch (error) {
            console.error("Error fetching locations:", error)
        }
    }

    const handleLocationChange = async (e) => {
        const locationId = e.target.value
        setSelectedLocation(locationId)
        setSelectedRoom("") // Reset room
        if (locationId) {
            try {
                const response = await api.get("/locations/rooms")
                const filtered = response.data.filter(r => r.location_id === parseInt(locationId))
                setRooms(filtered)
            } catch (error) {
                console.error("Error fetching rooms:", error)
            }
        } else {
            setRooms([])
        }
    }

    const handleRegisterRoom = async (e) => {
        e.preventDefault()
        if (!selectedLocation) return
        
        setRegistering(true)
        try {
            await api.put(`/users/${user.id}`, {
                loc_id: selectedLocation,
                room_id: selectedRoom || null 
            })
            
            // Reload to reflect changes
            window.location.reload() 
        } catch (error) {
            console.error("Error updating room:", error)
        } finally {
            setRegistering(false)
        }
    }

    const getInitials = (name) => {
        if (!name) return "U"
        return name.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2)
    }

    if (loading) {
        return <div className="loading">Loading dashboard...</div>
    }

    return (
        <div className="dashboard-layout employee-dashboard">


            <div className="dashboard-top-row">
                {/* Profile Card */}
                <div className="profile-card">
                    <div className="profile-header">
                        <div className="avatar-circle">
                            {getInitials(user?.name)}
                        </div>
                        <div className="profile-greeting">
                            <h3>Hi, {user?.name || "Employee"}</h3>
                            <span className="waving-hand">👋</span>
                            <span className="role-badge role-badge-employee">Employee</span>
                        </div>
                    </div>
                    <div className="profile-details">
                        <div className="info-row">
                            <span className="info-icon">📧</span> {user?.email}
                        </div>
                        <div className="info-row">
                            <span className="info-icon">📞</span> {user?.phone || "Not set"}
                        </div>
                        <div className="info-row">
                            <span className="info-icon">🛡️</span> {user?.role} - {user?.department || "General"}
                        </div>
                        <div className="info-row">
                            <span className="info-icon">📍</span> {user?.room_name || "Not assigned"}
                        </div>
                        <div className="info-row">
                            <span className="info-icon">🏢</span> {user?.location_name || "Not assigned"}
                        </div>
                    </div>
                    <div className="profile-footer">
                        <Link to="/profile">Update Profile →</Link>
                        <button onClick={() => setShowRoomModal(true)}>Update Location →</button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="stats-grid-2">
                    {/* My Assets */}
                    <div className="stat-widget">
                        <div>
                            <div className="stat-icon blue">💻</div>
                            <span className="stat-label">My Assets</span>
                            <h3 className="stat-value">{stats.assignedAssets}</h3>
                        </div>
                        <div className="stat-footer">
                            <Link to="/assets">View full details →</Link>
                        </div>
                    </div>

                    {/* Pending Requests */}
                    <div className="stat-widget">
                        <div>
                            <div className="stat-icon orange">⏳</div>
                            <span className="stat-label">Pending Req.</span>
                            <h3 className="stat-value">{stats.pendingRequests}</h3>
                        </div>
                        <div className="stat-footer">
                            <Link to="/requests">View full details →</Link>
                        </div>
                    </div>

                    {/* Approved Requests */}
                    <div className="stat-widget">
                        <div>
                            <div className="stat-icon green">✅</div>
                            <span className="stat-label">Approved</span>
                            <h3 className="stat-value">{stats.approvedRequests}</h3>
                        </div>
                        <div className="stat-footer">
                            <Link to="/requests">View full details →</Link>
                        </div>
                    </div>

                    {/* New Request */}
                    <div className="stat-widget">
                        <div>
                            <div className="stat-icon purple">✨</div>
                            <span className="stat-label">Total Requests</span>
                            <h3 className="stat-value">{stats.totalRequests}</h3>
                        </div>
                        <div className="stat-footer">
                            <Link to="/requests">Create New →</Link>
                        </div>
                    </div>
                </div>
            </div>

            {showRoomModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Register to Location & Room</h2>
                            <button className="close-modal" onClick={() => setShowRoomModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleRegisterRoom}>
                                <div style={{marginBottom: '1.5rem'}}>
                                    <label className="form-label">Select Location</label>
                                    <select 
                                        className="form-input" 
                                        value={selectedLocation} 
                                        onChange={handleLocationChange}
                                        required
                                    >
                                        <option value="">Select Location</option>
                                        {locations.map(loc => (
                                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                {selectedLocation && (
                                    <div>
                                        <label className="form-label">Select Room</label>
                                        {rooms.length > 0 ? (
                                            <div className="room-grid">
                                                {rooms.map(room => {
                                                    const occupancy = room.current_occupancy || 0
                                                    const capacity = room.capacity || 10
                                                    const percentage = Math.min((occupancy / capacity) * 100, 100)
                                                    const isSelected = selectedRoom === room.id
                                                    
                                                    return (
                                                        <div 
                                                            key={room.id} 
                                                            className={`room-card ${isSelected ? 'selected' : ''}`}
                                                            onClick={() => setSelectedRoom(room.id)}
                                                        >
                                                            <div className="room-name">{room.name}</div>
                                                            <div className="room-floor">Floor {room.floor}</div>
                                                            <div className="progress-container">
                                                                <div className="progress-label">
                                                                    <span>{occupancy}/{capacity}</span>
                                                                </div>
                                                                <div className="progress-bar-bg">
                                                                    <div 
                                                                        className={`progress-bar-fill ${percentage >= 100 ? 'full' : percentage >= 80 ? 'near-full' : ''}`} 
                                                                        style={{width: `${percentage}%`}}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        ) : (
                                            <p style={{color: '#94a3b8', fontStyle: 'italic'}}>No rooms available in this location.</p>
                                        )}
                                    </div>
                                )}

                                <div className="flex gap-2 mt-6">
                                    <button type="button" className="btn btn-secondary flex-1" onClick={() => setShowRoomModal(false)}>
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary flex-1" 
                                        disabled={registering || !selectedLocation}
                                        style={(!selectedLocation) ? {opacity: 0.5, cursor: 'not-allowed'} : {}}
                                    >
                                        {registering ? "Updating..." : "Update Location"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default EmployeeDashboard
